import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { RouteService } from 'src/app/services/route.service';
import { SingleSearchLocation } from 'src/app/_models/pcmws/singleSearch/singleSearchResponse';
import { StopLocation } from 'src/app/_models/state/stopState';
import { RouteTotals } from 'src/app/_models/view/Route';
import { ViewRouteStop } from 'src/app/_models/view/routeStop';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { SubscriptionLevel } from 'src/app/auth/models/user';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { RouteEditMode, RouteState } from 'src/app/_models/state/routeState';
import { FormBuilder, FormControl } from '@angular/forms';
import { stopDurationValidator } from 'src/app/_helpers/stop-duration-helper';
import { ApplicationSettingsService } from 'src/app/services/application-settings.service';
import { RoutingProfile } from 'src/app/_models/daws/trip';
import { Common, LngLatLike } from '@trimblemaps/trimblemaps-js';
import { MapService } from 'src/app/services/map.service';
import { CurrencyState } from 'src/app/_models/state/optionUIState';
import { optionsUI } from 'src/app/utils/optionsUI';
import { WarningView } from 'src/app/reports/_models/view/warningView';
import { ReportsComponent } from 'src/app/reports/components/reports/reports.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { GoogleAnalytics } from 'src/app/services/google-analytics.service';
import { Precision } from 'src/app/application-settings/components/general/general.component';

// TODO search row should be refactored into a separate component

enum InputMode {
  Search,
  Favorites,
  CustomPlaces,
}
@Component({
  selector: 'app-stop-list',
  templateUrl: './stop-list.component.html',
  styleUrls: ['./stop-list.component.scss'],
})
export class StopListComponent implements OnInit {
  @Input() routeId: number;
  @Input() routeTotals: RouteTotals;
  @Input() displayCurrency: CurrencyState;
  optionsUI = optionsUI;
  appSettingsData$: Observable<RoutingProfile>;
  // Enum references for template use
  routeEditModeEnum = RouteEditMode;
  inputModeEnum = InputMode;
  warningMessage: WarningView[] = [];
  routeList$: Observable<RouteState>;
  precision$: Observable<number>;

  hasPremiumSubscription$: Observable<boolean>;
  isEuRegion$: Observable<boolean>;
  routeEditMode$: Observable<RouteEditMode>;

  stops$: Observable<ViewRouteStop[]>;
  hasStops$: Observable<boolean>;
  hasEtaEtdValue = false;
  nbrStops = 0;

  private inputMode = new BehaviorSubject<InputMode>(InputMode.Search);
  inputMode$ = this.inputMode.asObservable();

  stopOptionsArray = this.formBuilder.array([]);
  stopListWithwarn$: Observable<WarningView[]>;

  constructor(
    private mapService: MapService,
    private routeService: RouteService,
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private applicationSettingsService: ApplicationSettingsService,
    private googleAnalytics: GoogleAnalytics,
    private modalService: BsModalService
  ) {
    this.hasPremiumSubscription$ = this.authenticationService.currentUser.pipe(
      map((user) =>
        user ? user.subscriptionLevel === SubscriptionLevel.PREMIUM : false
      )
    );
    this.isEuRegion$ = this.authenticationService.currentUser.pipe(
      map((user) => user?.region.toLowerCase() === Common.Region.EU)
    );
  }

  ngOnInit(): void {
    this.precision$ = this.applicationSettingsService.searchView$.pipe(
      map((searchOption) => searchOption?.precision)
    );
    const route$ = this.routeService.getRoute(this.routeId);
    const stopList$ = this.routeService.getStopList(this.routeId);
    this.routeList$ = this.routeService.routesState$.pipe(
      mergeMap((routeList) =>
        routeList.filter((route) => route.id === this.routeId)
      )
    );
    this.appSettingsData$ = this.applicationSettingsService.store$.pipe(
      map((data) => data)
    );
    this.stops$ = combineLatest([
      route$,
      stopList$,
      this.routeList$,
      this.applicationSettingsService.searchView$,
    ]).pipe(
      map(([route, stopList, routeList, searchOption]) => {
        this.stopOptionsArray.clear();
        this.nbrStops = stopList.length;
        const type = route.arrDepSelection?.type;

        if (routeList?.drivingDirectionsReport?.ReportLegs) {
          routeList.drivingDirectionsReport.ReportLegs.forEach(
            (reportLeg, index) => {
              let warningText;
              let inc = 0;
              let warningFinal;
              let warning = false;
              reportLeg.ReportLines.forEach((line) => {
                // combine all warnings per leg
                if (line?.Direction && line?.Direction.includes('Warning - ')) {
                  warning = true;
                  warningText = line.Direction.replace('Warning - ', '');
                  warningText = warningText.split(';');

                  let temp = [];
                  warningText.forEach((text) => {
                    temp = text.split(':');
                    temp[0] = temp[0].trim();
                    warningText.push(temp[0]);
                  });
                  warningText.splice(0, warningText.length / 2);
                  warningFinal = warningFinal ? warningFinal : [];
                  warningFinal = [
                    ...new Set([...warningFinal, ...warningText]),
                  ];
                }
              });
              warningFinal = warningFinal ? warningFinal.join(', ') : '';
              warningFinal = `Warning: ${warningFinal}`;
              if (warning) {
                inc++;
                const i = index + inc;
                this.warningMessage.push({
                  type: 'warning',
                  index: i,
                  legIndex: index,
                  text: warningFinal,
                });
              }
            }
          );
        }
        if (
          type === this.optionsUI.arrDepTypes[1].value ||
          type === this.optionsUI.arrDepTypes[2].value ||
          type === this.optionsUI.arrDepTypes[3].value
        ) {
          this.hasEtaEtdValue = true;
        }
        return stopList.map((stop, index, list) => {
          let highlightStop = false;
          if (
            list.length - 1 === index &&
            this.routeService.stopTimeStamp !== null &&
            this.routeService.stopTimeStamp.routeId === this.routeId
          ) {
            const now = Date.now();
            if (now - this.routeService.stopTimeStamp.time < 500) {
              highlightStop = true;
            }
          }
          const formControl = new FormControl(stop.stopTime, [
            stopDurationValidator(),
          ]);
          this.stopOptionsArray.push(formControl);
          // Add some view specific properties
          // TODO replace numStops with more specific properties?

          stop.miles = Number(stop?.miles).toFixed(
            Number(
              searchOption?.precision
                ? searchOption?.precision
                : Precision.Hundredths
            )
          );
          return {
            ...stop,
            numStops: list.length,
            routeColor: route.color,
            editMode: route.editMode,
            formControl,
            highlightStop,
            index,
          };
        });
      })
    );

    this.stopListWithwarn$ = this.stops$.pipe(
      map((stops) => {
        this.warningMessage = this.getUniqueListBy(
          this.warningMessage,
          'index'
        );
        const stopListWithwarn: WarningView[] = [];
        stops.forEach((stop, index) => {
          const warnIndex =
            index < this.warningMessage.length
              ? index
              : this.warningMessage.length - 1;
          stopListWithwarn.push({ ...stops[index] });
          if (
            this.warningMessage.length &&
            index === this.warningMessage[warnIndex].legIndex
          ) {
            stopListWithwarn.push({
              type: 'warning',
              value: this.warningMessage[warnIndex]?.text,
            });
          }
        });
        return stopListWithwarn;
      })
    );

    this.hasStops$ = stopList$.pipe(map((stopList) => stopList.length > 0));

    this.routeEditMode$ = route$.pipe(map((route) => route.editMode));
  }

  changeInputMode(mode: InputMode) {
    // If the new mode is the same, reset to Search (default)
    this.inputMode.next(
      mode === this.inputMode.getValue() ? InputMode.Search : mode
    );
  }

  addStops(locations: SingleSearchLocation[]) {
    locations.forEach((location) => {
      this.addStop(location);
    });
  }

  addStop(location: SingleSearchLocation) {
    const stop: StopLocation = {
      name: location?.PlaceName,
      address: {
        streetAddress: location.Address.StreetAddress,
        city: location.Address.City,
        stateAbbreviation: location.Address.State,
        zip: location.Address.Zip,
        county: location.Address.County,
        countryAbbreviation: location.Address.Country,
      },
      coords: [
        Number.parseFloat(location.Coords.Lon),
        Number.parseFloat(location.Coords.Lat),
      ],
    };
    this.routeService.addStopToRoute(this.routeId, stop);
    if (this.nbrStops === 1) {
      this.centerStop(stop?.coords);
    }
    this.googleAnalytics.addGA(
      'Add_Stop',
      'Add_Stop_Search',
      'Single Search',
      'Add stop by single search'
    );
  }

  deleteStop(stopIndex: number) {
    this.routeService.deleteStopFromRoute(this.routeId, stopIndex);
    this.googleAnalytics.addGA(
      'Delete_Stop',
      'Delete_Stop_click',
      'Route',
      'Delete stop clicked'
    );
  }
  getUniqueListBy(arr, key) {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  }

  centerStop(lngLat: LngLatLike) {
    this.mapService.setMapCenter(lngLat);
    this.mapService.zoomTo(15); // Legacy zoom is 16, which is the same as 15 here.
    this.googleAnalytics.addGA(
      'Show_On_Map',
      'Show_On_Map_click',
      'Route',
      'Show on map clicked'
    );
  }

  drop(event: CdkDragDrop<string[]>) {
    this.routeService.rearrangeRouteStops(
      this.routeId,
      event.previousIndex,
      event.currentIndex
    );
  }

  showReport(event) {
    if (event) {
      this.modalService.show(ReportsComponent, {
        class: 'modal-xl reports-modal',
        ignoreBackdropClick: true,
        initialState: {
          routeId: this.routeId,
          displayCurrency: this.displayCurrency,
          showWarningReport: true,
        },
      });
    }
  }
  saveEdit() {
    if (this.stopOptionsArray.valid) {
      this.routeService.updateRouteStopTime(
        this.routeId,
        this.stopOptionsArray.value
      );
      this.routeService.setRouteEditMode(this.routeId, RouteEditMode.AddStop);
    }
  }

  cancelEdit() {
    this.routeService.setRouteEditMode(this.routeId, RouteEditMode.AddStop);
  }
}
